import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const blank = {
  merchant_id: "", title: "", description: "", category: "", cashback_rate: 5,
  coupon_code: "", expiration: "", featured: false, ai_priority: 50, terms: "",
};

export default function OffersAdmin() {
  const [offers, setOffers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [o, m] = await Promise.all([api.get("/catalog/offers?limit=200"), api.get("/catalog/merchants?limit=200")]);
    setOffers(o.data);
    setMerchants(m.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const startEdit = (o) => { setEditing(o); setForm({ ...blank, ...o }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        cashback_rate: parseFloat(form.cashback_rate),
        ai_priority: parseFloat(form.ai_priority),
      };
      if (editing) {
        await api.put(`/catalog/offers/${editing.id}`, payload);
        toast.success("Offer updated");
      } else {
        await api.post("/catalog/offers", payload);
        toast.success("Offer created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete offer?")) return;
    await api.delete(`/catalog/offers/${id}`);
    load();
  };

  return (
    <div data-testid="adm-offers-page">
      <PageHeader
        eyebrow="Catalog"
        title="Offer management"
        subtitle="Featured deals, coupons, and AI-prioritized offers."
        action={<Button onClick={startCreate} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> New offer</Button>}
      />

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold">Title</th>
                <th className="px-5 py-3 font-semibold">Merchant</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Cashback</th>
                <th className="px-5 py-3 font-semibold">AI Priority</th>
                <th className="px-5 py-3 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} className="border-b border-gray-100">
                  <td className="px-5 py-3 font-medium">{o.title} {o.featured && <Badge className="ml-2 bg-amber-50 text-amber-800 border border-amber-200">Featured</Badge>}</td>
                  <td className="px-5 py-3 text-gray-600">{o.merchant_name}</td>
                  <td className="px-5 py-3 capitalize text-gray-600">{o.category}</td>
                  <td className="px-5 py-3 text-emerald-700 font-semibold">{o.cashback_rate}%</td>
                  <td className="px-5 py-3">{o.ai_priority}</td>
                  <td className="px-5 py-3 flex gap-1">
                    <button onClick={() => startEdit(o)} className="p-1.5 text-gray-500 hover:text-emerald-700"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(o.id)} className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit offer" : "New offer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Merchant</Label>
              <Select value={form.merchant_id} onValueChange={(v) => setForm({ ...form, merchant_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select merchant" /></SelectTrigger>
                <SelectContent>
                  {merchants.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label>Cashback rate (%)</Label><Input type="number" step="0.5" value={form.cashback_rate} onChange={(e) => setForm({ ...form, cashback_rate: e.target.value })} /></div>
              <div><Label>Coupon code</Label><Input value={form.coupon_code || ""} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} /></div>
              <div><Label>AI priority</Label><Input type="number" min="0" max="100" value={form.ai_priority} onChange={(e) => setForm({ ...form, ai_priority: e.target.value })} /></div>
            </div>
            <div><Label>Terms</Label><Textarea value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
            </label>
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
